// components/Sidebar.jsx
// ─── v4 — your v3 + Referral nav item under new EARN section ──

import { useEffect, useRef, useState, useCallback } from 'react';
import logo1 from '../images/kfx (Logo) (3).png';

// ─── Nav definition ──────────────────────────────────────────
// Grouping rationale (usage-first):
//   OVERVIEW  — single home base, visited every session
//   JOURNAL   — daily core workflow: log trades + capture notes (used together)
//   ANALYSIS  — post-session review: deep stats + live charting
//   TOOLS     — utility/simulation belt, used occasionally
//   AI        — premium feature, warrants its own spotlight section
//   GROW      — referral / community, clearly separated from trading workflow
const NAV = [
  // ── OVERVIEW ──────────────────────────────────────────────
  {
    id: 'd', section: 'OVERVIEW', label: 'Dashboard',
    icon: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7">
        <rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/>
        <rect x="14" y="14" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/>
      </svg>
    ),
  },

  // ── JOURNAL — daily logging workflow ──────────────────────
  {
    id: 'j', section: 'JOURNAL', label: 'Trade Journal',
    icon: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
        <polyline points="14 2 14 8 20 8"/>
        <line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/>
      </svg>
    ),
    badgeType: 'count',
  },
  {
    id: 'nb', section: 'JOURNAL', label: 'Notebook',
    icon: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7">
        <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/>
        <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/>
      </svg>
    ),
    badgeType: 'draft',
  },

  // ── ANALYSIS — post-session review ────────────────────────
  {
    id: 'analytics', section: 'ANALYSIS', label: 'Analytics',
    icon: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7">
        <line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/>
        <line x1="6" y1="20" x2="6" y2="14"/><line x1="2" y1="20" x2="22" y2="20"/>
      </svg>
    ),
  },
  {
    id: 'chart', section: 'ANALYSIS', label: 'Chart',
    icon: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7">
        <rect x="3" y="3" width="18" height="18" rx="1"/>
        <polyline points="9 14 12 11 15 14"/>
        <line x1="12" y1="11" x2="12" y2="20"/><path d="M5 8h14"/>
      </svg>
    ),
    badgeType: 'tv',
  },

  // ── TOOLS — utility belt, occasional use ──────────────────
  {
    id: 'tools', section: 'TOOLS', label: 'Tools',
    icon: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7">
        <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/>
      </svg>
    ),
    badgeType: 'tools',
  },
  {
    id: 'sim', section: 'TOOLS', label: 'What-If Sim',
    icon: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7">
        <polyline points="22 7 13.5 15.5 8.5 10.5 2 17"/>
        <polyline points="16 7 22 7 22 13"/>
        <line x1="2" y1="21" x2="22" y2="21"/>
      </svg>
    ),
    badgeType: 'new',
  },
  {
    id: 'indicators', section: 'TOOLS', label: 'Indicators',
    icon: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7">
        <path d="M3 3v18h18"/>
        <path d="M7 16l4-4 4 4 4-8"/>
      </svg>
    ),
    badgeType: 'indicators',
  },

  // ── AI — premium spotlight ─────────────────────────────────
  {
    id: 'ai', section: 'AI', label: 'AI KFX',
    icon: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7">
        <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
      </svg>
    ),
    badgeType: 'ai',
  },

  // ── GROW — referral / community ───────────────────────────
  {
    id: 'referral', section: 'GROW', label: 'Refer & Earn',
    icon: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
        <circle cx="9" cy="7" r="4"/>
        <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
        <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
      </svg>
    ),
    badgeType: 'referral',
  },
];

// ─── Icons ───────────────────────────────────────────────────
const IcoSettings = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7">
    <circle cx="12" cy="12" r="3"/>
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
  </svg>
);

const IcoLogout = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7">
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
    <polyline points="16 17 21 12 16 7"/>
    <line x1="21" y1="12" x2="9" y2="12"/>
  </svg>
);

const initials = name =>
  name ? name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2) : 'KF';

// ─── Inline styles for nav items ──────────────────────────────
const navItemBase = {
  display: 'flex', alignItems: 'center', gap: 10,
  padding: '8px 12px',
  margin: '1px 8px',
  borderRadius: 6,
  fontSize: 12.5, letterSpacing: '.01em',
  cursor: 'pointer', userSelect: 'none',
  border: 'none', background: 'none',
  color: 'rgba(245,240,232,.48)',
  width: 'calc(100% - 16px)',
  textAlign: 'left',
  transition: 'color .16s, background .16s, transform .14s',
  position: 'relative',
  fontFamily: "'Inter', sans-serif",
  fontWeight: 500,
  animation: 'sbItemIn .3s ease both',
  overflow: 'hidden',
};

const navItemHoverStyle = {
  color: 'rgba(245,240,232,.76)',
  background: 'rgba(212,168,67,.05)',
  transform: 'translateX(2px)',
};

const navItemActiveStyle = {
  color: 'var(--gold)',
  background: 'linear-gradient(90deg, rgba(212,168,67,.12), rgba(212,168,67,.03))',
};

// ─── NavItem with hover state ─────────────────────────────────
function NavItemFixed({ item, isActive, onClick, delay, children }) {
  const [hovered, setHovered] = useState(false);

  const style = {
    ...navItemBase,
    animationDelay: delay,
    ...(isActive ? navItemActiveStyle : (hovered ? navItemHoverStyle : {})),
  };

  return (
    <button
      style={style}
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {isActive && (
        <span style={{
          position: 'absolute', left: 0, top: '15%', bottom: '15%',
          width: 3, borderRadius: '0 3px 3px 0',
          background: 'linear-gradient(180deg, transparent, var(--gold), transparent)',
          boxShadow: '0 0 8px rgba(212,168,67,.55)',
        }}/>
      )}
      {children}
    </button>
  );
}

// ─── Badge components ─────────────────────────────────────────

function BadgeCount({ count }) {
  if (!count || count < 1) return null;
  return (
    <span style={{
      marginLeft: 'auto', flexShrink: 0,
      background: 'var(--gold)', color: 'var(--black)',
      fontSize: 9, fontWeight: 800, fontFamily: "'Inter',sans-serif",
      padding: '2px 7px', borderRadius: 10, letterSpacing: '.02em',
      animation: 'badgePop .3s cubic-bezier(.4,0,.2,1) both',
      minWidth: 20, textAlign: 'center',
    }}>
      {count > 999 ? '999+' : count}
    </span>
  );
}

// Notebook badge: amber pulsing "DRAFT" when unsaved drafts exist,
// gold count pill when notes exist but no unsaved drafts.
function BadgeDraft({ count, hasDraft }) {
  if (hasDraft) {
    return (
      <span style={{
        marginLeft: 'auto', flexShrink: 0,
        display: 'flex', alignItems: 'center', gap: 4,
        fontSize: 8, fontWeight: 700, letterSpacing: '.06em',
        color: '#D4A843', fontFamily: "'Inter',sans-serif",
        background: 'rgba(212,168,67,.10)',
        border: '1px solid rgba(212,168,67,.22)',
        padding: '2px 6px', borderRadius: 4,
      }}>
        <span style={{
          width: 5, height: 5, borderRadius: '50%', flexShrink: 0,
          background: '#D4A843',
          animation: 'pulseDot 2.2s ease infinite',
          boxShadow: '0 0 5px rgba(212,168,67,.7)',
        }}/>
        DRAFT
      </span>
    );
  }
  if (!count || count < 1) return null;
  return (
    <span style={{
      marginLeft: 'auto', flexShrink: 0,
      background: 'var(--gold)', color: 'var(--black)',
      fontSize: 9, fontWeight: 800, fontFamily: "'Inter',sans-serif",
      padding: '2px 7px', borderRadius: 10, letterSpacing: '.02em',
      minWidth: 20, textAlign: 'center',
    }}>
      {count > 999 ? '999+' : count}
    </span>
  );
}

function BadgeLive() {
  return (
    <span style={{
      marginLeft: 'auto', flexShrink: 0,
      display: 'flex', alignItems: 'center', gap: 4,
      fontSize: 8, fontWeight: 700, letterSpacing: '.08em',
      color: 'var(--green)', fontFamily: "'Inter',sans-serif",
      background: 'rgba(46,204,138,.10)', border: '1px solid rgba(46,204,138,.22)',
      padding: '2px 6px', borderRadius: 4,
    }}>
      <span style={{
        width: 5, height: 5, borderRadius: '50%', flexShrink: 0,
        background: 'var(--green)',
        animation: 'pulseDot 1.8s ease infinite',
        boxShadow: '0 0 5px var(--green)',
      }}/>
      LIVE
    </span>
  );
}

function BadgeTools() {
  return (
    <span style={{
      marginLeft: 'auto', flexShrink: 0,
      fontSize: 8, fontWeight: 700, padding: '2px 6px', borderRadius: 4,
      background: 'rgba(212,168,67,.1)', color: 'var(--gold)',
      border: '1px solid rgba(212,168,67,.2)',
      fontFamily: "'Inter',sans-serif", letterSpacing: '.06em',
    }}>7</span>
  );
}

function BadgeNew() {
  return (
    <span style={{
      marginLeft: 'auto', flexShrink: 0,
      fontSize: 7.5, fontWeight: 700, letterSpacing: '.1em',
      color: 'var(--gold)', fontFamily: "'Inter',sans-serif",
      background: 'linear-gradient(135deg,rgba(212,168,67,.22),rgba(212,168,67,.07))',
      border: '1px solid rgba(212,168,67,.28)',
      padding: '2px 6px', borderRadius: 4,
      position: 'relative', overflow: 'hidden',
    }}>
      NEW
      <span style={{
        position: 'absolute', inset: 0, pointerEvents: 'none',
        background: 'linear-gradient(90deg,transparent 20%,rgba(212,168,67,.25) 50%,transparent 80%)',
        animation: 'shimmer 2.4s ease infinite',
      }}/>
    </span>
  );
}

function BadgeAI() {
  return (
    <span style={{
      marginLeft: 'auto', flexShrink: 0,
      fontSize: 7.5, fontWeight: 700, letterSpacing: '.1em',
      color: 'var(--gold)', fontFamily: "'Inter',sans-serif",
      background: 'linear-gradient(135deg,rgba(212,168,67,.26),rgba(212,168,67,.08))',
      border: '1px solid rgba(212,168,67,.28)',
      padding: '2px 7px', borderRadius: 4,
      animation: 'goldPulse 2.8s ease infinite',
    }}>
      AI
    </span>
  );
}

function BadgeEarn() {
  return (
    <span style={{
      marginLeft: 'auto', flexShrink: 0,
      fontSize: 7.5, fontWeight: 700, letterSpacing: '.1em',
      color: '#4CD97A', fontFamily: "'Inter',sans-serif",
      background: 'rgba(60,200,100,.10)',
      border: '1px solid rgba(60,200,100,.25)',
      padding: '2px 7px', borderRadius: 4,
    }}>
      EARN
    </span>
  );
}

function BadgeIndicators() {
  return (
    <span style={{
      marginLeft: 'auto', flexShrink: 0,
      fontSize: 7.5, fontWeight: 700, letterSpacing: '.08em',
      color: '#5B8FE8', fontFamily: "'Inter',sans-serif",
      background: 'rgba(91,143,232,.10)',
      border: '1px solid rgba(91,143,232,.22)',
      padding: '2px 6px', borderRadius: 4,
    }}>
      3
    </span>
  );
}

// 0 referrals → green EARN tag; 1+ → gold count pill
function BadgeReferral({ count }) {
  if (!count || count < 1) return <BadgeEarn />;
  return (
    <span style={{
      marginLeft: 'auto', flexShrink: 0,
      background: 'var(--gold)', color: 'var(--black)',
      fontSize: 9, fontWeight: 800, fontFamily: "'Inter',sans-serif",
      padding: '2px 7px', borderRadius: 10, letterSpacing: '.02em',
      animation: 'badgePop .3s cubic-bezier(.4,0,.2,1) both',
      minWidth: 20, textAlign: 'center',
    }}>
      {count > 999 ? '999+' : count}
    </span>
  );
}

// ─── XP progress bar ─────────────────────────────────────────
const LEVEL_XP = { Student: 15, Apprentice: 35, Trader: 60, Expert: 80, Master: 95 };
function XPBar({ level }) {
  const pct = LEVEL_XP[level] ?? 30;
  return (
    <div style={{ marginTop: 5, height: 2, background: 'rgba(255,255,255,.06)', borderRadius: 2, overflow: 'hidden' }}>
      <div style={{
        height: '100%', width: `${pct}%`,
        background: 'linear-gradient(90deg, var(--gold-dim), var(--gold))',
        borderRadius: 2, transition: 'width .6s ease',
      }}/>
    </div>
  );
}

// ─── Icon box ────────────────────────────────────────────────
const iconBoxBase = {
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  width: 28, height: 28, borderRadius: 5, flexShrink: 0,
  background: 'rgba(255,255,255,.04)', transition: 'background .16s',
};
const iconBoxActive = { background: 'rgba(212,168,67,.13)' };
const iconBoxHover  = { background: 'rgba(212,168,67,.07)' };

// ─── Account type meta (mirrors SettingsPage) ────────────────
const ACCT_TYPE_META = {
  Live:        { color: 'var(--green, #2ECC8A)', bg: 'rgba(46,204,138,.12)',  border: 'rgba(46,204,138,.25)',  dot: '#2ECC8A' },
  Demo:        { color: 'var(--blue,  #5B8FE8)', bg: 'rgba(91,143,232,.12)',  border: 'rgba(91,143,232,.25)',  dot: '#5B8FE8' },
  'Prop Firm': { color: 'var(--gold,  #D4A843)', bg: 'rgba(212,168,67,.12)',  border: 'rgba(212,168,67,.25)',  dot: '#D4A843' },
  Paper:       { color: 'rgba(200,192,168,.6)',   bg: 'rgba(200,192,168,.07)', border: 'rgba(200,192,168,.18)', dot: 'rgba(200,192,168,.5)' },
};

function AccountTypeDot({ type }) {
  const meta = ACCT_TYPE_META[type] || ACCT_TYPE_META['Live'];
  return (
    <span style={{
      display: 'inline-block',
      width: 6, height: 6,
      borderRadius: '50%',
      background: meta.dot,
      flexShrink: 0,
      boxShadow: `0 0 5px ${meta.dot}`,
    }} />
  );
}

function AccountTypeBadge({ type }) {
  const meta = ACCT_TYPE_META[type] || ACCT_TYPE_META['Live'];
  return (
    <span style={{
      fontSize: 8, fontWeight: 800, letterSpacing: '.07em',
      color: meta.color, background: meta.bg,
      border: `1px solid ${meta.border}`,
      padding: '1px 5px', borderRadius: 3,
      textTransform: 'uppercase', flexShrink: 0,
      fontFamily: "'DM Sans', sans-serif",
    }}>{type}</span>
  );
}

// ─── AccountSwitcher ─────────────────────────────────────────
function AccountSwitcher({ tradingAccounts = [], activeAccountId, onSetActive, onAddAccount, onManage, isOnAccountsPage }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const activeAcct = tradingAccounts.find(a => a.id === activeAccountId);

  // Close popover on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div
      ref={ref}
      style={{
        borderTop: '1px solid rgba(255,255,255,.05)',
        borderBottom: '1px solid rgba(255,255,255,.05)',
        padding: '4px 0',
        flexShrink: 0,
        position: 'relative',
      }}
    >
      {/* ── Trigger button ── */}
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          display: 'flex', alignItems: 'center', gap: 9,
          padding: '8px 12px',
          margin: '1px 8px',
          width: 'calc(100% - 16px)',
          background: (open || isOnAccountsPage) ? 'rgba(212,168,67,.07)' : 'none',
          border: (open || isOnAccountsPage) ? '1px solid rgba(212,168,67,.22)' : '1px solid transparent',
          borderRadius: 7,
          cursor: 'pointer',
          transition: 'all .15s',
          textAlign: 'left',
        }}
        onMouseEnter={e => { if (!open) { e.currentTarget.style.background = 'rgba(255,255,255,.03)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,.06)'; }}}
        onMouseLeave={e => { if (!open) { e.currentTarget.style.background = 'none'; e.currentTarget.style.borderColor = 'transparent'; }}}
      >
        {/* Icon box */}
        <span style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          width: 28, height: 28, borderRadius: 6, flexShrink: 0,
          background: activeAcct ? ACCT_TYPE_META[activeAcct.type]?.bg || 'rgba(212,168,67,.1)' : 'rgba(255,255,255,.04)',
          border: `1px solid ${activeAcct ? ACCT_TYPE_META[activeAcct.type]?.border || 'rgba(212,168,67,.2)' : 'rgba(255,255,255,.06)'}`,
          fontSize: 13,
        }}>
          {activeAcct ? (
            { Live: '📈', Demo: '🧪', 'Prop Firm': '🏆', Paper: '📄' }[activeAcct.type] || '🗂️'
          ) : (
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="rgba(200,192,168,.4)" strokeWidth="1.8">
              <path d="M3 9h18M7 3v3m10-3v3M3 5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5z"/>
            </svg>
          )}
        </span>

        {/* Label */}
        <span style={{ flex: 1, minWidth: 0 }}>
          {activeAcct ? (
            <>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'rgba(240,234,214,.85)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: "'Inter', sans-serif" }}>
                {activeAcct.name}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 2 }}>
                <AccountTypeDot type={activeAcct.type} />
                <span style={{ fontSize: 10, color: 'rgba(200,192,168,.45)', fontFamily: "'Inter', sans-serif" }}>
                  {activeAcct.type}{activeAcct.balance ? ` · $${parseFloat(activeAcct.balance).toLocaleString()}` : ''}
                </span>
              </div>
            </>
          ) : (
            <div style={{ fontSize: 12, color: 'rgba(200,192,168,.4)', fontFamily: "'Inter', sans-serif" }}>
              No account selected
            </div>
          )}
        </span>

        {/* Chevron */}
        <svg
          width="12" height="12" viewBox="0 0 24 24" fill="none"
          stroke="rgba(200,192,168,.35)" strokeWidth="2"
          style={{ flexShrink: 0, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .2s' }}
        >
          <polyline points="6 9 12 15 18 9"/>
        </svg>
      </button>

      {/* ── Popover ── */}
      {open && (
        <div style={{
          position: 'absolute',
          bottom: 'calc(100% + 4px)',
          left: 8, right: 8,
          background: 'linear-gradient(180deg, #141412 0%, #0f0f0d 100%)',
          border: '1px solid rgba(255,255,255,.1)',
          borderRadius: 10,
          boxShadow: '0 -8px 32px rgba(0,0,0,.6), 0 0 0 1px rgba(212,168,67,.06)',
          overflow: 'hidden',
          zIndex: 200,
          animation: 'fadeUp .15s ease',
        }}>

          {/* Account list */}
          {tradingAccounts.length > 0 ? (
            <div style={{ padding: '6px 0' }}>
              {tradingAccounts.map(acct => {
                const isActive = acct.id === activeAccountId;
                const meta = ACCT_TYPE_META[acct.type] || ACCT_TYPE_META['Live'];
                return (
                  <button
                    key={acct.id}
                    onClick={() => { onSetActive(acct.id); setOpen(false); }}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      width: '100%', padding: '9px 12px',
                      background: isActive ? 'rgba(212,168,67,.07)' : 'transparent',
                      border: 'none', cursor: 'pointer',
                      textAlign: 'left',
                      transition: 'background .12s',
                    }}
                    onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = 'rgba(255,255,255,.04)'; }}
                    onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = 'transparent'; }}
                  >
                    <span style={{
                      width: 30, height: 30, flexShrink: 0,
                      background: meta.bg, border: `1px solid ${meta.border}`,
                      borderRadius: 7, display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 14,
                    }}>
                      {{ Live: '📈', Demo: '🧪', 'Prop Firm': '🏆', Paper: '📄' }[acct.type] || '🗂️'}
                    </span>
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ fontSize: 12, fontWeight: 600, color: isActive ? 'var(--gold, #D4A843)' : 'rgba(240,234,214,.8)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: "'Inter', sans-serif" }}>
                          {acct.name}
                        </span>
                        <AccountTypeBadge type={acct.type} />
                      </div>
                      {acct.balance && (
                        <div style={{ fontSize: 10, color: 'rgba(200,192,168,.4)', marginTop: 1, fontFamily: "'Inter', sans-serif" }}>
                          ${parseFloat(acct.balance).toLocaleString()}
                          {acct.broker ? ` · ${acct.broker}` : ''}
                        </div>
                      )}
                    </span>
                    {isActive && (
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--gold, #D4A843)" strokeWidth="2.5" style={{ flexShrink: 0 }}>
                        <polyline points="20 6 9 17 4 12"/>
                      </svg>
                    )}
                  </button>
                );
              })}
            </div>
          ) : (
            <div style={{ padding: '16px 12px', textAlign: 'center' }}>
              <div style={{ fontSize: 11, color: 'rgba(200,192,168,.4)', marginBottom: 10, lineHeight: 1.6, fontFamily: "'Inter', sans-serif" }}>
                No accounts yet.<br/>Add your first to get started.
              </div>
            </div>
          )}

          {/* Divider + actions */}
          <div style={{ borderTop: '1px solid rgba(255,255,255,.06)', padding: '6px' }}>
            {tradingAccounts.length < 10 && (
              <button
                onClick={() => { onAddAccount(); setOpen(false); }}
                style={{
                  display: 'flex', alignItems: 'center', gap: 7,
                  width: '100%', padding: '8px 10px',
                  background: 'rgba(212,168,67,.08)', border: '1px solid rgba(212,168,67,.2)',
                  color: 'var(--gold, #D4A843)', fontSize: 11, fontWeight: 700,
                  cursor: 'pointer', borderRadius: 7,
                  fontFamily: "'DM Sans', sans-serif", letterSpacing: '.04em',
                  marginBottom: 5,
                  transition: 'all .12s',
                }}
                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(212,168,67,.14)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'rgba(212,168,67,.08)'; }}
              >
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                  <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
                </svg>
                ADD ACCOUNT
              </button>
            )}
            <button
              onClick={() => { onManage(); setOpen(false); }}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                width: '100%', padding: '8px 10px',
                background: 'transparent', border: '1px solid rgba(255,255,255,.07)',
                color: 'rgba(200,192,168,.5)', fontSize: 11, fontWeight: 500,
                cursor: 'pointer', borderRadius: 7,
                fontFamily: "'DM Sans', sans-serif",
                transition: 'all .12s',
              }}
              onMouseEnter={e => { e.currentTarget.style.color = 'rgba(240,234,214,.75)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,.14)'; }}
              onMouseLeave={e => { e.currentTarget.style.color = 'rgba(200,192,168,.5)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,.07)'; }}
            >
              <span>Manage Accounts</span>
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
              </svg>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}


export function SidebarContent({ page, setPage, badges = {}, onClose, userProfile, onLogout, tradingAccounts, activeAccountId, onSetActiveAccount, onAddAccount }) {
  const sections = [...new Set(NAV.map(n => n.section))];
  const name  = userProfile?.name  || 'Trader';
  const level = userProfile?.level || 'Student';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>

      {/* ── Logo ── */}
      <div className="sb-logo" style={{ flexShrink: 0 }}>
        <div className="sb-logo-mark">
          <img src={logo1} alt="KFX" style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }} />
        </div>
        <div className="sb-brand-wrap">
          <div className="sb-brand">KFX Journal</div>
          <div className="sb-sub">Journaling platform</div>
        </div>
        {onClose && (
          <button className="sb-close-btn" onClick={onClose} aria-label="Close menu">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        )}
      </div>

      {/* ── Nav ── */}
      <nav className="sb-nav" style={{ overflowY: 'auto', overflowX: 'hidden', WebkitOverflowScrolling: 'touch', flex: 1, minHeight: 0 }}>
        {sections.map((sec, si) => (
          <div key={sec} className="sb-section">
            {/* Section label — AI and GROW get special accent treatment */}
            <div className="sb-section-label" style={
              sec === 'AI' ? {
                color: 'rgba(212,168,67,.65)',
                letterSpacing: '.12em',
              } : sec === 'GROW' ? {
                color: 'rgba(76,217,122,.55)',
                letterSpacing: '.12em',
              } : {}
            }>{sec}</div>

            {NAV.filter(n => n.section === sec).map((item, ni) => {
              const isActive = page === item.id;
              // AI item gets a subtle gold glow backdrop when active
              const isAIItem = sec === 'AI';
              const isGrowItem = sec === 'GROW';
              return (
                <NavItemFixed
                  key={item.id}
                  item={item}
                  isActive={isActive}
                  onClick={() => { setPage(item.id); onClose?.(); }}
                  delay={`${(si * 3 + ni) * 45}ms`}
                >
                  <span style={{
                    ...iconBoxBase,
                    ...(isActive ? iconBoxActive : {}),
                    // AI item icon gets a persistent subtle glow
                    ...(isAIItem && !isActive ? { background: 'rgba(212,168,67,.08)', border: '1px solid rgba(212,168,67,.12)' } : {}),
                    // GROW item icon gets a green tint
                    ...(isGrowItem && !isActive ? { background: 'rgba(76,217,122,.07)', border: '1px solid rgba(76,217,122,.1)' } : {}),
                  }}>
                    {item.icon}
                  </span>
                  <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {item.label}
                  </span>

                  {/* Contextual badges */}
                  {item.badgeType === 'tools'      && <BadgeTools />}
                  {item.badgeType === 'indicators' && <BadgeIndicators />}
                  {item.badgeType === 'count' && <BadgeCount count={badges[item.id]} />}
                  {item.badgeType === 'draft'  && <BadgeDraft count={badges.nbCount} hasDraft={badges.nb > 0} />}
                  {item.badgeType === 'tv'     && <BadgeLive />}
                  {item.badgeType === 'new'    && <BadgeNew />}
                  {item.badgeType === 'ai'     && <BadgeAI />}
                  {item.badgeType === 'earn'   && <BadgeEarn />}
                  {item.badgeType === 'referral' && <BadgeReferral count={badges.referral} />}
                  {!item.badgeType && badges[item.id] > 0 && <BadgeCount count={badges[item.id]} />}
                </NavItemFixed>
              );
            })}
          </div>
        ))}
      </nav>

      {/* ── Settings ── */}
      <div style={{ borderTop: '1px solid rgba(255,255,255,.05)', padding: '6px 0', flexShrink: 0 }}>
        <NavItemFixed
          item={{ id: 'settings' }}
          isActive={page === 'settings'}
          onClick={() => { setPage('settings'); onClose?.(); }}
          delay="0ms"
        >
          <span style={{ ...iconBoxBase, ...(page === 'settings' ? iconBoxActive : {}) }}>
            <IcoSettings />
          </span>
          <span style={{ flex: 1 }}>Settings</span>
        </NavItemFixed>
      </div>

      {/* ── Account Switcher ── */}
      <AccountSwitcher
        tradingAccounts={tradingAccounts || []}
        activeAccountId={activeAccountId}
        onSetActive={(id) => { onSetActiveAccount?.(id); }}
        onAddAccount={() => { onAddAccount?.(); onClose?.(); }}
        onManage={() => { setPage('accounts'); onClose?.(); }}
        isOnAccountsPage={page === 'accounts'}
      />

      {/* ── User footer ── */}
      <div className="sb-user">
        <div className="sb-avatar">{initials(name)}</div>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div className="sb-user-name">{name}</div>
          <div className="sb-user-level">
            <span className="sb-level-dot"/>
            {level}
          </div>
          <XPBar level={level} />
        </div>
        <button className="sb-logout" onClick={onLogout} title="Logout" aria-label="Logout">
          <IcoLogout />
        </button>
      </div>

    </div>
  );
}

// ─────────────────────────────────────────────────────────────
//  Desktop sidebar wrapper
// ─────────────────────────────────────────────────────────────
export function Sidebar({ page, setPage, badges, onLogout, userProfile, tradingAccounts, activeAccountId, onSetActiveAccount, onAddAccount }) {
  return (
    <div className="sidebar" style={{ display: 'flex', flexDirection: 'column' }}>
      <SidebarContent page={page} setPage={setPage} badges={badges} onLogout={onLogout} userProfile={userProfile} tradingAccounts={tradingAccounts} activeAccountId={activeAccountId} onSetActiveAccount={onSetActiveAccount} onAddAccount={onAddAccount} />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
//  Mobile slide-in sidebar (swipe-to-close + blur overlay)
// ─────────────────────────────────────────────────────────────
export function MobileSidebar({ page, setPage, badges, open, onClose, onLogout, userProfile, tradingAccounts, activeAccountId, onSetActiveAccount, onAddAccount }) {
  const sidebarRef  = useRef(null);
  const touchStartX = useRef(null);
  const onCloseRef  = useRef(onClose);
  useEffect(() => { onCloseRef.current = onClose; }, [onClose]);

  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  useEffect(() => {
    const el = sidebarRef.current;
    if (!el) return;
    const onTouchStart = e => { touchStartX.current = e.touches[0].clientX; };
    const onTouchEnd   = e => {
      if (touchStartX.current === null) return;
      if (touchStartX.current - e.changedTouches[0].clientX > 55) onCloseRef.current();
      touchStartX.current = null;
    };
    el.addEventListener('touchstart', onTouchStart, { passive: true });
    el.addEventListener('touchend',   onTouchEnd,   { passive: true });
    return () => {
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchend',   onTouchEnd);
    };
  }, []);

  const handleNavigate = useCallback((p) => {
    setPage(p);
    onCloseRef.current();
  }, [setPage]);

  return (
    <>
      <div
        onClick={onClose}
        style={{
          position: 'fixed', inset: 0, zIndex: 89,
          opacity: open ? 1 : 0,
          pointerEvents: open ? 'auto' : 'none',
          transition: 'opacity .25s ease',
          background: 'rgba(0,0,0,.65)',
          backdropFilter: 'blur(6px)',
          WebkitBackdropFilter: 'blur(6px)',
        }}
      />
      <div
        ref={sidebarRef}
        style={{
          position: 'fixed', left: 0, top: 0, bottom: 0,
          width: 270, zIndex: 100,
          display: 'flex', flexDirection: 'column',
          height: '100dvh',
          background: 'linear-gradient(180deg, #0c0c0a 0%, #080808 100%)',
          borderRight: '1px solid var(--border)',
          overflow: 'hidden',
          transform: open ? 'translateX(0)' : 'translateX(-100%)',
          transition: 'transform .24s cubic-bezier(.25,.46,.45,.94)',
          paddingLeft: 'env(safe-area-inset-left, 0)',
        }}
      >
        <SidebarContent
          page={page}
          setPage={handleNavigate}
          badges={badges}
          onClose={onClose}
          onLogout={onLogout}
          userProfile={userProfile}
          tradingAccounts={tradingAccounts}
          activeAccountId={activeAccountId}
          onSetActiveAccount={onSetActiveAccount}
          onAddAccount={onAddAccount}
        />
      </div>
    </>
  );
}

// ─────────────────────────────────────────────────────────────
//  Bottom nav — mobile only
// ─────────────────────────────────────────────────────────────
const BOT_NAV = [
  { id: 'd',         label: 'Home',    icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/></svg> },
  { id: 'j',         label: 'Journal', icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg> },
  { id: '__fab__',   label: '',        icon: null },
  { id: 'chart',     label: 'Chart',   icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><rect x="3" y="3" width="18" height="18" rx="1"/><polyline points="9 14 12 11 15 14"/><line x1="12" y1="11" x2="12" y2="20"/><path d="M5 8h14"/></svg> },
  { id: 'analytics', label: 'Stats',   icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/><line x1="2" y1="20" x2="22" y2="20"/></svg> },
  { id: 'ai',        label: 'AI',      icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg> },
];

export function BottomNav({ page, setPage, onNewTrade }) {
  return (
    <nav className="bottom-nav">
      {BOT_NAV.map(item => {
        if (item.id === '__fab__') return (
          <button key="fab" className="bn-fab" onClick={onNewTrade} aria-label="Log trade">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
            </svg>
          </button>
        );
        return (
          <button
            key={item.id}
            className={`bn-item${page === item.id ? ' active' : ''}`}
            onClick={() => setPage(item.id)}
          >
            {item.icon}
            <span>{item.label}</span>
          </button>
        );
      })}
    </nav>
  );
}